import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { X } from 'lucide-react';
import ApiService from '../../components/api_services/apiService.jsx';
import {
  setShowEditModal,
  setViewedNotification,
  toggleNeedRefresh,
} from '../../store/notificationsTabSlice';
import './Notifications.css';

function EditNotificationModal({ notificationId }) {
  const dispatch = useDispatch();
  const showEditModal = useSelector((state) => state.notifications.showEditModal);

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'SINGULAR',
    is_recurring: false,
    recurrence_pattern: '',
    scheduled_send_date: '',
    next_send_date: '',
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (showEditModal && notificationId) {
      loadNotification();
    }
  }, [showEditModal, notificationId]);

  const loadNotification = async () => {
    setLoadingData(true);
    try {
      const campaign = await ApiService.getNotificationById(notificationId);
      setFormData({
        title: campaign.title || '',
        message: campaign.message || '',
        type: campaign.type || 'SINGULAR',
        is_recurring: campaign.is_recurring || false,
        recurrence_pattern: campaign.recurrence_pattern || '',
        scheduled_send_date: campaign.scheduled_send_date
          ? new Date(campaign.scheduled_send_date).toISOString().slice(0, 16)
          : '',
        next_send_date: campaign.next_send_date
          ? new Date(campaign.next_send_date).toISOString().slice(0, 16)
          : '',
      });
    } catch (error) {
      console.error('Błąd podczas ładowania kampanii:', error);
      alert('Nie udało się załadować danych kampanii.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleClose = () => {
    dispatch(setShowEditModal(false));
    dispatch(setViewedNotification(null));
    setFormData({
      title: '',
      message: '',
      type: 'SINGULAR',
      is_recurring: false,
      recurrence_pattern: '',
      scheduled_send_date: '',
      next_send_date: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {};

      if (formData.title) payload.title = formData.title;
      if (formData.message) payload.message = formData.message;
      if (formData.type) payload.type = formData.type;
      if (formData.is_recurring !== undefined) payload.is_recurring = formData.is_recurring;
      if (formData.recurrence_pattern) payload.recurrence_pattern = formData.recurrence_pattern;
      if (formData.scheduled_send_date) {
        const date = new Date(formData.scheduled_send_date);
        payload.scheduled_send_date = date.toISOString();
      }
      if (formData.next_send_date) {
        const date = new Date(formData.next_send_date);
        payload.next_send_date = date.toISOString();
      }

      await ApiService.updateNotification(notificationId, payload);
      alert('Kampania została zaktualizowana.');
      handleClose();
      dispatch(toggleNeedRefresh());
      // Reload the page to refresh the list
      window.location.reload();
    } catch (error) {
      console.error('Błąd podczas aktualizacji kampanii:', error);
      alert('Nie udało się zaktualizować kampanii.');
    } finally {
      setLoading(false);
    }
  };

  if (!showEditModal) return null;

  if (loadingData) {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content notification-modal" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
          <h2 className="modal-title">Edytuj powiadomienie</h2>
          <div className="loading-message">Ładowanie danych...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content notification-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>
          <X size={20} />
        </button>
        <h2 className="modal-title">Edytuj powiadomienie</h2>

        <form onSubmit={handleSubmit} className="notification-form">
          <div className="form-field">
            <label>Tytuł</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              maxLength={255}
              placeholder="Wprowadź tytuł powiadomienia"
            />
          </div>

          <div className="form-field">
            <label>Wiadomość</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              maxLength={5000}
              rows={5}
              placeholder="Wprowadź treść powiadomienia"
            />
          </div>

          <div className="form-field">
            <label>Typ</label>
            <select
              value={formData.type}
              onChange={(e) => {
                const newType = e.target.value;
                setFormData({
                  ...formData,
                  type: newType,
                  is_recurring: newType === 'RECURRING',
                });
              }}
            >
              <option value="SINGULAR">Pojedyncze</option>
              <option value="RECURRING">Powtarzające się</option>
            </select>
          </div>

          {formData.type === 'RECURRING' && (
            <>
              <div className="form-field">
                <label>Wzorzec powtarzania</label>
                <select
                  value={formData.recurrence_pattern}
                  onChange={(e) => setFormData({ ...formData, recurrence_pattern: e.target.value })}
                >
                  <option value="">Wybierz wzorzec</option>
                  <option value="DAILY">Codziennie</option>
                  <option value="WEEKLY">Co tydzień</option>
                  <option value="MONTHLY">Co miesiąc</option>
                </select>
              </div>

              <div className="form-field">
                <label>Następna data wysłania</label>
                <input
                  type="datetime-local"
                  value={formData.next_send_date}
                  onChange={(e) => setFormData({ ...formData, next_send_date: e.target.value })}
                />
              </div>
            </>
          )}

          {formData.type === 'SINGULAR' && (
            <div className="form-field">
              <label>Zaplanowana data wysłania</label>
              <input
                type="datetime-local"
                value={formData.scheduled_send_date}
                onChange={(e) => setFormData({ ...formData, scheduled_send_date: e.target.value })}
              />
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={handleClose} disabled={loading}>
              Anuluj
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditNotificationModal;

